import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowRight, Eye, EyeOff, Target, Users } from 'lucide-react';
import BackButton from '../../components/BackButton';
import BrandLogo from '../../components/BrandLogo';

export default function LoginPage() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Flash message when arriving from an accepted invitation, an
    // email-verification success, etc. We consume the router state so a
    // manual refresh does not re-fire the toast.
    useEffect(() => {
        const flash = location.state?.flash;
        if (flash === 'invitation_accepted') {
            toast.success(t('accept_invitation.success_toast'));
            navigate(location.pathname, { replace: true, state: {} });
        } else if (flash === 'password_reset') {
            toast.success(t('reset_password.success_toast'));
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password);

        if (result.success) {
            navigate('/dashboard');
        } else {
            // Use the structured backend error code (never the raw message)
            // to decide the flow. Prevents [object Object] and lets us
            // route the user to the resend page in any language.
            if (result.errorCode === 'email_not_verified') {
                navigate('/verify-email-required', { state: { email, emailSent: true } });
                setLoading(false);
                return;
            }
            const localized = result.errorCode
                ? t(`auth.errors.${result.errorCode}`, { defaultValue: result.error || t('auth.errors.generic') })
                : (result.error || t('auth.errors.generic'));
            setError(localized);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex" data-testid="login-page">
            {/* Left Panel - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-[#002FA7] p-12 flex-col justify-between">
                <div>
                    <Link to="/" className="flex items-center gap-3">
                        <BrandLogo size={40} />
                        <span className="text-2xl font-bold text-white font-['Outfit']">GUIRETAL</span>
                    </Link>
                </div>
                
                <div className="space-y-8">
                    <h1 className="text-4xl font-bold text-white font-['Outfit'] leading-tight whitespace-pre-line">
                        {t('auth.branding_login_title')}
                    </h1>
                    <p className="text-white/80 text-lg">
                        {t('auth.branding_login_subtitle')}
                    </p>
                    
                    <div className="space-y-4">
                        <div className="flex items-center gap-4 text-white/90">
                            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                                <Target className="w-5 h-5" />
                            </div>
                            <span>{t('auth.branding_feature_scoring')}</span>
                        </div>
                        <div className="flex items-center gap-4 text-white/90">
                            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                                <Users className="w-5 h-5" />
                            </div>
                            <span>{t('auth.branding_feature_collaboration')}</span>
                        </div>
                    </div>
                </div>
                
                <p className="text-white/60 text-sm">
                    {t('landing.footer_copyright')}
                </p>
            </div>

            {/* Right Panel - Login Form */}
            <div className="flex-1 flex items-start justify-center p-4 sm:p-8 bg-[#F9FAFB] relative">
                <div className="hidden lg:block absolute top-6 left-6">
                    <BackButton to="/" data-testid="login-back-button" />
                </div>
                <div className="w-full max-w-md">
                    {/* Mobile: BackButton + Logo aligned */}
                    <div className="lg:hidden mb-6 flex items-center justify-between">
                        <BackButton to="/" data-testid="login-back-button-mobile" />
                        <Link to="/" className="flex items-center gap-2">
                            <BrandLogo size={36} />
                            <span className="text-xl font-bold text-[#0A0A0A] font-['Outfit']">GUIRETAL</span>
                        </Link>
                    </div>

                    <div className="bg-white p-8 rounded-lg border border-[#E5E7EB] shadow-sm">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                                {t('auth.login_title')}
                            </h2>
                            <p className="text-[#4B5563] mt-2">
                                {t('auth.login_subtitle')}
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-[#FEE2E2] border border-[#EF4444]/20 rounded-lg" data-testid="login-error">
                                <p className="text-[#DC2626] text-sm">{error}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-[#0A0A0A]">{t('auth.email')}</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder={t('auth.placeholder_email_login')}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="border-[#E5E7EB] focus:border-[#002FA7] focus:ring-[#002FA7]/10"
                                    data-testid="login-email-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password" className="text-[#0A0A0A]">{t('auth.password')}</Label>
                                    <Link to="/forgot-password" className="text-sm text-[#002FA7] hover:underline">
                                        {t('auth.forgot_password')}
                                    </Link>
                                </div>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="border-[#E5E7EB] focus:border-[#002FA7] focus:ring-[#002FA7]/10 pr-10"
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[#002FA7] hover:bg-[#002480] text-white h-11"
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        {t('auth.sign_in_loading')}
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        {t('auth.sign_in')}
                                        <ArrowRight className="w-4 h-4" />
                                    </span>
                                )}
                            </Button>
                        </form>

                        <p className="text-center mt-6 text-[#4B5563]">
                            {t('auth.no_account')}{' '}
                            <Link to="/register" className="text-[#002FA7] font-medium hover:underline">
                                {t('auth.create_account_link')}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
